import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const DEFAULT_DELAY_MS = 10_000;
const TOAST_OVERRUN_MS = 3000;

type UseUndoableDeleteOptions<T extends { id: string }> = {
  // One-line label for the toast (e.g. `"Region NH deleted"`). The toast
  // description automatically reads "Undoing in 10s…" so callers don't need
  // to spell that out.
  describe: (item: T) => string;
  // Performs the actual DELETE. Called once the undo window elapses, or
  // synchronously on unmount if a delete was still pending — we'd rather
  // err on "fire the commit" than "silently swallow the intent."
  commit: (item: T) => Promise<unknown>;
  // Called after a successful commit. Typically invalidates the relevant
  // TanStack Query so the table refetches without the deleted row.
  onCommitSuccess?: (item: T) => void;
  // Called when commit throws — usually a "still in use" or auth error from
  // the API. The hook automatically restores the row's visibility (pulls
  // its id back out of pendingIds) and surfaces the error to the user. The
  // callback exists for callers that want to add logging / instrumentation.
  onCommitError?: (item: T, error: unknown) => void;
  // The "in use" protection some endpoints implement returns a thrown
  // string; everything else may be an Error. Callers can pass a formatter
  // that decides how the error text reads on screen.
  formatError?: (error: unknown) => string;
  delayMs?: number;
};

// Manages an undoable delete flow on a list of items. The caller hands over
// a delete function ("commit"); the hook handles the toast, the pending-id
// tracking, the 10-second timer, and the cleanup on unmount.
//
// Returns:
//   - pendingIds — Set of item ids currently in the undo window. Callers
//     filter their rendered list to exclude these so the row disappears
//     immediately on click and reappears if Undo or commit-fail fires.
//   - enqueue(item) — start the undo flow.
//
// The hook intentionally commits any still-pending delete on unmount.
// Navigating away from the page is treated as "I'm done here" — the
// alternative (silently dropping the pending delete) would surprise the
// user who already saw the row disappear.
export function useUndoableDelete<T extends { id: string }>(
  options: UseUndoableDeleteOptions<T>,
): { pendingIds: Set<string>; enqueue: (item: T) => void } {
  const { describe, commit, onCommitSuccess, onCommitError, formatError, delayMs } = options;
  const [pendingIds, setPendingIds] = useState<Set<string>>(() => new Set());
  // Holds the timer handle + the item itself, keyed by id. The item is
  // captured so the unmount-flush path knows what to commit.
  const queueRef = useRef<
    Map<string, { timer: ReturnType<typeof setTimeout>; item: T; toastId: string | number }>
  >(new Map());
  // Keep the callbacks fresh without re-running the unmount effect on every
  // render. Reading from a ref inside the effect cleanup is the cleanest
  // way to flush pending deletes without coupling that cleanup to the
  // current render's closure.
  const callbacksRef = useRef({ commit, onCommitSuccess, onCommitError, formatError });
  callbacksRef.current = { commit, onCommitSuccess, onCommitError, formatError };

  function runCommit(item: T) {
    const cb = callbacksRef.current;
    Promise.resolve(cb.commit(item))
      .then(() => cb.onCommitSuccess?.(item))
      .catch((err) => {
        cb.onCommitError?.(item, err);
        const message = cb.formatError
          ? cb.formatError(err)
          : err instanceof Error
            ? err.message
            : "Delete failed";
        toast.error(message);
        // Restore the row's visibility so the user can see / retry / fix.
        setPendingIds((prev) => {
          const next = new Set(prev);
          next.delete(item.id);
          return next;
        });
      });
  }

  function removeFromPending(id: string) {
    setPendingIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }

  function enqueue(item: T) {
    // If a previous undo window is still open for the same id (rapid double
    // delete), cancel the prior timer + toast first so we don't double-fire.
    const prior = queueRef.current.get(item.id);
    if (prior) {
      clearTimeout(prior.timer);
      toast.dismiss(prior.toastId);
    }

    setPendingIds((prev) => {
      if (prev.has(item.id)) return prev;
      const next = new Set(prev);
      next.add(item.id);
      return next;
    });

    const window = delayMs ?? DEFAULT_DELAY_MS;
    const timer = setTimeout(() => {
      queueRef.current.delete(item.id);
      runCommit(item);
    }, window);

    const toastId = toast(describe(item), {
      description: `Undo to keep it. Otherwise it'll be deleted in ${Math.round(window / 1000)}s.`,
      action: {
        label: "Undo",
        onClick: () => {
          // If the timer already fired (race window between the timer
          // and the click handler), the entry is gone and the commit is
          // already in flight or done. Restoring the row in the UI here
          // would flicker it back briefly before the success-path query
          // invalidation removes it again — and the user would think
          // their Undo worked when it didn't. Surface a short note and
          // bail.
          const entry = queueRef.current.get(item.id);
          if (!entry) {
            toast("Already deleted — Undo came in too late to stop it.");
            return;
          }
          clearTimeout(entry.timer);
          queueRef.current.delete(item.id);
          removeFromPending(item.id);
        },
      },
      duration: window + TOAST_OVERRUN_MS,
    });

    queueRef.current.set(item.id, { timer, item, toastId });
  }

  // On unmount, flush any pending commits so navigating away doesn't drop
  // the user's intent. We clear the timers first (so they don't fire after
  // unmount and try to setState on a dead component), then call commit
  // directly.
  //
  // Unmount path intentionally diverges from the timer-fired path:
  //   • Success — fire onCommitSuccess so the caller's query invalidation
  //     still runs (queryClient is a singleton in a higher provider, so
  //     this works even with the component gone). Next time the user
  //     visits the page, the cache has been refreshed.
  //   • Failure — call onCommitError (so logging still happens) but
  //     SUPPRESS the global toast.error. The user has navigated to a
  //     different page and no longer has visual context for the deleted
  //     row; surfacing "Failed to delete X" on, say, a Policy dashboard
  //     they just opened is more confusing than helpful. The row will
  //     reappear naturally on next visit because the cache invalidation
  //     in the success path never fired — the server still has the row,
  //     and the next list fetch picks it up.
  useEffect(() => {
    const queue = queueRef.current;
    return () => {
      for (const { timer, item, toastId } of queue.values()) {
        clearTimeout(timer);
        toast.dismiss(toastId);
        const cb = callbacksRef.current;
        Promise.resolve(cb.commit(item))
          .then(() => cb.onCommitSuccess?.(item))
          .catch((err) => {
            cb.onCommitError?.(item, err);
          });
      }
      queue.clear();
    };
  }, []);

  return { pendingIds, enqueue };
}
